import * as vegaLite from "vega-lite";
import * as vega from "vega";
import type { ImageOps } from "./image/types";

// Default image operations (will be overridden by environment-specific adapters)
const defaultImageOps: ImageOps = {
  async svgToPng(_svg: string): Promise<string> {
    throw new Error(
      "Image operations not configured. Use environment-specific imports: datashows-sdk/node, datashows-sdk/edge, or datashows-sdk/browser"
    );
  },
  isAvailable(): boolean {
    return false;
  },
};

// Auto-detect environment and use appropriate adapter
function detectEnvironment(): ImageOps {
  // Check for Edge runtime
  if (typeof (globalThis as any).EdgeRuntime !== "undefined") {
    try {
      const { edgeImageOps } = require("./image/adapters/edge");
      return edgeImageOps;
    } catch (error) {
      console.warn("Edge adapter not available, falling back to default");
    }
  }

  // Check for Node.js environment
  if (typeof process !== "undefined" && process.versions?.node) {
    try {
      const { nodeImageOps } = require("./image/adapters/node");
      return nodeImageOps;
    } catch (error) {
      console.warn("Node adapter not available, falling back to default");
    }
  }

  // Check for browser environment
  if (typeof window !== "undefined") {
    try {
      const { browserImageOps } = require("./image/adapters/browser");
      return browserImageOps;
    } catch (error) {
      console.warn("Browser adapter not available, falling back to default");
    }
  }

  return defaultImageOps;
}

// Auto-detect and set the appropriate image operations
export let imageOps: ImageOps = detectEnvironment();

// -------- Server-side chart generation via OpenAI → Vega-Lite → SVG/PNG --------

export type ConversationMessage = {
  role: "user" | "assistant";
  content: string;
  chartData?: unknown;
  vegaSpec?: any;
};

export type GenerateChartOptions = {
  data: unknown;
  prompt: string;
  fileType?: "svg" | "png";
  outputFormat?: "image" | "chartData"; // NEW: Support external chart libraries
  model?: string;
  apiKey?: string;
  isSubscribed?: boolean;

  // NEW: Conversational context support
  conversationHistory?: ConversationMessage[];
  previousChartContext?: {
    lastChartData?: unknown;
    lastChartSpec?: any;
  };
};

export type ChartDataResult = {
  kind: "chartData";
  library: string; // e.g., "apexcharts", "chartjs", "recharts", "shadcn"
  data: any; // Processed data ready for the library
  config: any; // Chart configuration for the library
  explanation?: string; // AI explanation of what was done
};

export type GeneratedChartResult =
  | {
      kind: "image";
      fileType: "svg" | "png";
      dataUrl: string;
      vegaSpec?: any; // NEW: Return the spec so it can be used in conversation history
      explanation?: string; // NEW: AI explanation of what was done
    }
  | ChartDataResult;

function extractJsonFromText(text: string, isSubscribed: boolean = false): any {
  const fenceMatch = text.match(/```json[\s\S]*?```/i);
  const raw = fenceMatch
    ? fenceMatch[0].replace(/```json|```/gi, "").trim()
    : text;

  const start = raw.indexOf("{");
  const end = raw.lastIndexOf("}");

  if (start >= 0 && end > start) {
    let jsonStr = raw.slice(start, end + 1);

    // Size guard
    const maxJsonSize = isSubscribed ? 200000 : 50000;
    if (jsonStr.length > maxJsonSize) {
      throw new Error(
        `Generated JSON is too large and likely truncated. Please try with a smaller dataset or different prompt. ${
          isSubscribed
            ? ""
            : "Consider upgrading your plan for larger datasets."
        }`
      );
    }

    // First attempt
    try {
      return JSON.parse(jsonStr);
    } catch {}

    // Pre-clean common issues: comments, stray backticks, single quotes, trailing commas
    try {
      const cleaned = jsonStr
        .replace(/\/\/[^]*?\n/g, "\n") // line comments
        .replace(/\/\*[\s\S]*?\*\//g, "") // block comments
        .replace(/`/g, '"') // backticks → quotes
        // keys in single quotes
        .replace(/([\{\[,\s])'([^'\n]*)'(?=\s*:)/g, '$1"$2"')
        // string values in single quotes
        .replace(/:\s*'([^'\n]*)'/g, ': "$1"')
        // trailing commas before } or ]
        .replace(/,(\s*[}\]])/g, "$1");
      return JSON.parse(cleaned);
    } catch {}

    // Fallback: try to recover a complete JSON object by brace counting
    const lines = jsonStr.split("\n");
    let completeJson = "";
    let braceCount = 0;
    let inJson = false;

    for (const line of lines) {
      if (line.trim().startsWith("{")) inJson = true;
      if (inJson) {
        completeJson += line + "\n";
        braceCount += (line.match(/\{/g) || []).length;
        braceCount -= (line.match(/\}/g) || []).length;
        if (braceCount === 0 && line.trim().endsWith("}")) break;
      }
    }

    if (completeJson.trim()) {
      try {
        return JSON.parse(completeJson.trim());
      } catch (parseError) {
        // Final fix-up: remove trailing commas and close missing braces
        let fixedJson = completeJson.trim().replace(/,(\s*[}\]])/g, "$1");
        if (braceCount > 0) fixedJson += "}".repeat(braceCount);
        try {
          return JSON.parse(fixedJson);
        } catch (finalError) {
          throw new Error(
            `Invalid JSON generated: ${
              parseError instanceof Error ? parseError.message : "Unknown error"
            }`
          );
        }
      }
    }

    throw new Error("Could not extract valid JSON from the response");
  }

  throw new Error("No valid JSON found in the response");
}

function ensureProperDimensions(spec: any): any {
  // Ensure the spec has proper dimensions for readability
  if (!spec.width || spec.width < 400) {
    spec.width = 600;
  }
  if (!spec.height || spec.height < 300) {
    spec.height = 400;
  }

  // For charts with many data points, use larger dimensions
  if (
    spec.data?.values &&
    Array.isArray(spec.data.values) &&
    spec.data.values.length > 20
  ) {
    spec.width = Math.max(spec.width, 800);
    spec.height = Math.max(spec.height, 500);
  }

  return spec;
}

async function requestSpecFromAPI(options: GenerateChartOptions): Promise<{
  spec: string;
  explanation?: string;
  library?: string;
  data?: any;
  config?: any;
}> {
  const url =
    process.env.ENV === "local"
      ? `${process.env.BASE_URL}/api/spec/generate`
      : `https://app.datashows.ai/api/spec/generate`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // Internal auth header so server-to-server calls bypass session/key checks
      "x-internal-authorized": "1",
    },
    body: JSON.stringify({
      data: options.data,
      prompt: options.prompt,
      isSubscribed: options.isSubscribed,
      outputFormat: options.outputFormat,
      // NEW: Pass conversation context
      conversationHistory: options.conversationHistory,
      previousChartContext: options.previousChartContext,
    }),
  });
  if (!res.ok) {
    const t = await res.text().catch(() => "");
    throw new Error(`Spec API failed: ${res.status} ${t}`);
  }
  const json = (await res.json().catch(() => null)) as any;
  if (!json?.content) throw new Error("Spec API returned empty content");
  return {
    spec: json.content as string,
    explanation: json.explanation,
    library: json.library,
    data: json.data,
    config: json.config,
  };
}

async function renderVegaLiteToSvg(vlSpec: any): Promise<string> {
  // Check if we're in Edge runtime where Vega's eval() is restricted
  if (typeof (globalThis as any).EdgeRuntime !== "undefined") {
    // In Edge runtime, return a simple SVG placeholder
    // This is a fallback when Vega can't run due to eval() restrictions
    const width = vlSpec.width || 400;
    const height = vlSpec.height || 300;
    return `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <rect width="100%" height="100%" fill="#f0f0f0"/>
      <text x="50%" y="50%" text-anchor="middle" dy=".3em" font-family="Arial, sans-serif" font-size="14" fill="#666">
        Chart rendering not available in Edge runtime
      </text>
    </svg>`;
  }

  const compiled = vegaLite.compile(vlSpec, { config: {} }).spec;
  const runtime = vega.parse(compiled);
  const view = new vega.View(runtime, { renderer: "none" });
  const svg = await view.toSVG();
  return svg;
}

async function svgToPngDataUrl(svg: string): Promise<string> {
  return await imageOps.svgToPng(svg);
}

export async function generateChart(
  options: GenerateChartOptions
): Promise<GeneratedChartResult> {
  const outputFormat = options.outputFormat || "image";

  // NEW: Support for external chart libraries
  if (outputFormat === "chartData") {
    const { explanation, library, data, config } = await requestSpecFromAPI(
      options
    );

    if (!library || !data || !config) {
      throw new Error(
        "Chart data format requires library, data, and config from API"
      );
    }

    return {
      kind: "chartData",
      library,
      data,
      config,
      explanation,
    };
  }

  // Original image generation flow
  const fileType = options.fileType;
  const { spec: vlSpec, explanation } = await generateVegaLiteSpecWithOpenAI(
    options
  );

  const svg = await renderVegaLiteToSvg(vlSpec);
  if (fileType === "svg") {
    const dataUrl = `data:image/svg+xml;base64,${Buffer.from(svg).toString(
      "base64"
    )}`;
    return {
      kind: "image",
      fileType: "svg",
      dataUrl,
      vegaSpec: vlSpec,
      explanation,
    };
  }

  const dataUrl = await svgToPngDataUrl(svg);
  return {
    kind: "image",
    fileType: "png",
    dataUrl,
    vegaSpec: vlSpec,
    explanation,
  };
}

// Replace direct OpenAI call with server-spec API call
async function generateVegaLiteSpecWithOpenAI(
  options: GenerateChartOptions
): Promise<{ spec: any; explanation?: string }> {
  const { spec: content, explanation } = await requestSpecFromAPI(options);
  const spec = extractJsonFromText(content, options.isSubscribed);
  return {
    spec: ensureProperDimensions(spec),
    explanation,
  };
}
